import { useMemo } from 'react';
import { useLocation } from 'react-router';

// Types
import { SearchRequest } from 'components/Facets/types';

type ReturnType = string[] | string | boolean;

/**
 * @function convertType
 * @param { isArray, val }
 * @description - A helper function to convert to primitive types.
 */
const convertType = ({ isArray, val }: { isArray: boolean; val: string }): ReturnType => {
  // Convert to array.
  if (isArray) {
    return val.split(',').map((encodedVal) => decodeURIComponent(encodedVal));
  }
  // Convert to `true` boolean.
  if (val === 'true') {
    return true;
  }

  // Convert to `false` boolean.
  if (val === 'false') {
    return false;
  }

  // Otherwise it's a string.
  return decodeURIComponent(val);
};

const regex = /([^?=&]+)(=([^&]*))?/g;

/**
 * @function useSearchPayloadFromQueryString
 * @return ReturnType
 * @description - Looks at the the search param query and constructs a payload for an initial search for facets.
 * @example
 * search = ?providerName=Steve&startDate=2021-02-02&boards[]=ABIM,ABA&isMOC=false
 * returns {
 *   providerName: "Steve",
 *   startDate: "2021-02-02",
 *   boards: ["ABIM", "ABA"],
 *   isMOC: false,
 * }
 */
export const useSearchPayloadFromQueryString = <T extends any>(): SearchRequest => {
  const { search } = useLocation();

  // @ts-ignore
  return useMemo(
    () =>
      // Split on `=` and construct a `payload`.
      (search.match(regex) || [])
        // @ts-ignore
        .reduce((result: T, each: string): T => {
          const [key, val] = each.split('=');

          // Strip out the `[]` from the key name.
          const validKey: string = key.replace(/\[]/g, '');
          const isArray: boolean = key.includes('[]');

          // Set the value based on the inferred type.
          (result as any)[validKey] = convertType({ isArray, val });

          return result;
        }, {} as T),
    [search],
  );
};
